// substitute-constants — the pure value-substitution helper for the tweak surface
// (tweak-activity [4.1]). No DOM, no network — imports cleanly under Node, so it is
// unit-tested directly (test/editor/substitute-constants.test.js).
//
// substituteConstants(files, controls, values) rewrites the main file's TOP-LEVEL
// constant assignments to the student's chosen values, returning a NEW files array
// (the input is never mutated). It is the safety boundary of the tweak activity:
//
//   - It rewrites ONLY assignments whose name is a teacher-declared control AND a valid
//     Python identifier (`^[A-Za-z_][A-Za-z0-9_]*$`). Defence in depth: the route Joi
//     already enforces this pattern, and re-checking here means a control name can never
//     carry regex metacharacters or hijack an unintended line.
//   - Each value is emitted as a Python LITERAL (number / True|False / quoted string /
//     (r, g, b) tuple) — never concatenated as code, so a control can never inject
//     arbitrary Python.
//   - A declared name whose top-level assignment is not found is skipped cleanly.
//   - A value that cannot be encoded for its control type (e.g. an unparseable colour)
//     is skipped cleanly. Nothing throws; worst case a control is a no-op.

var IDENT_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

// Pick the main file to rewrite: prefer an entry named "main.py", else the first file.
function mainFileIndex(files) {
  if (!Array.isArray(files) || !files.length) { return -1; }
  for (var i = 0; i < files.length; i++) {
    if (files[i] && files[i].name === 'main.py') { return i; }
  }
  return 0;
}

// A Python string literal for an arbitrary JS string — single-quoted, with the
// metacharacters that would break out of / corrupt the literal escaped.
function pyString(s) {
  var str = (s == null) ? '' : String(s);
  return "'" + str
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/\r/g, '\\r')
    .replace(/\n/g, '\\n')
    .replace(/\t/g, '\\t') + "'";
}

// Parse a #rrggbb (or #rgb) hex to an "(r, g, b)" Python tuple literal, else null.
function pyColorTuple(hex) {
  if (typeof hex !== 'string') { return null; }
  var m = hex.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(m)) {
    m = m.split('').map(function (c) { return c + c; }).join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(m)) { return null; }
  var r = parseInt(m.slice(0, 2), 16);
  var g = parseInt(m.slice(2, 4), 16);
  var b = parseInt(m.slice(4, 6), 16);
  return '(' + r + ', ' + g + ', ' + b + ')';
}

// Encode a control's chosen value as a Python literal for its type. Returns null when
// the value can't be encoded (caller then skips that control cleanly).
function pyLiteral(type, value) {
  switch (type) {
    case 'toggle':
      return value ? 'True' : 'False';
    case 'colour': {
      // A hex string is the stored/edited form; a value that is already an (r,g,b)-ish
      // string is passed through unrecognised → skip. Only #rrggbb/#rgb produce a tuple.
      return pyColorTuple(value);
    }
    case 'number':
    case 'slider': {
      var n = Number(value);
      if (!isFinite(n)) { return null; }
      return String(n);
    }
    case 'choice':
    case 'text':
    default:
      return pyString(value);
  }
}

// Rewrite the single top-level assignment `^<name>\s*=.*$` in `source` to
// `<name> = <literal>`. Returns the (possibly unchanged) source. Only the FIRST such
// top-level assignment is replaced — a re-declaration lower down is left alone.
function rewriteAssignment(source, name, literal) {
  // `name` is a validated identifier, so it is regex-safe to interpolate directly.
  var re = new RegExp('^' + name + '\\s*=.*$', 'm');
  if (!re.test(source)) { return source; }
  return source.replace(re, name + ' = ' + literal);
}

export function substituteConstants(files, controls, values) {
  var list = Array.isArray(files)
    ? files.map(function (f) { return { name: f && f.name, content: (f && f.content) || '' }; })
    : [];
  var idx = mainFileIndex(list);
  if (idx < 0) { return list; }

  var source = list[idx].content;
  var vals = values || {};
  (Array.isArray(controls) ? controls : []).forEach(function (control) {
    if (!control || !IDENT_RE.test(control.name || '')) { return; }
    var raw = Object.prototype.hasOwnProperty.call(vals, control.name)
      ? vals[control.name] : control.default;
    var literal = pyLiteral(control.type, raw);
    if (literal == null) { return; }
    source = rewriteAssignment(source, control.name, literal);
  });

  list[idx] = { name: list[idx].name, content: source };
  return list;
}
